import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import 'yet-another-react-lightbox/styles.css';
import { Calendar } from 'primereact/calendar';
import { FaCheck } from 'react-icons/fa';
import { FiLink } from 'react-icons/fi';
import { Link, useParams, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import * as Yup from 'yup';
import Aos from 'aos';
import dayjs from 'dayjs';
import DOMPurify from 'dompurify';
import Lottie from 'lottie-react';
import React, { useEffect, useRef, useState } from 'react';
import Slider from 'react-slick';

import { yupResolver } from '@hookform/resolvers/yup';

import { all_routes } from '../router/all_routes';
import { AppDispatch, RootState } from '../../core/data/redux/store';
import {
  generateTimeOptions,
  formatDateWithTime,
} from '../../core/data/redux/actions/commonActions';
import {
  getVehicleByVin,
  addContactUs,
  resetContactUs,
} from '../../core/data/redux/action';
import { setSidebarFilters } from '../../core/data/redux/actions/sidebarFilterActions';
import CarImage from '../../core/data/modals/carImage';
import ContactConfirm from '../../core/data/modals/contactConfirm';
import EnquiryForm from '../../core/data/modals/enquiry';
import ImageWithBasePath from '../../core/data/img/ImageWithBasePath';
import Loader from '../../core/data/img/car_loading.json';
import PolicyForm from '../../core/data/modals/policy';

const listingDetails = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { vin } = useParams();
  const [open, setOpen] = useState(false);
  const [isMobile] = useState(window.innerWidth <= 767);
  const sidebarFilterState = useSelector(
    (state: RootState) => state.sidebarFilter
  );
  const selectedFilters = sidebarFilterState?.selectedFilters || {};

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const {
    user,
    systemSettings,
    loading,
    contactUsLoading,
    contactUsSuccess,
    contactUsError,
    carDetail,
  } = useSelector((state: RootState) => ({
    carDetail: state.vehicle.carDetail,
    loading: state.vehicle.loading,
    contactUsSuccess: state.contactUs.contactUsSuccess,
    contactUsError: state.contactUs.contactUsError,
    contactUsLoading: state.contactUs.contactUsLoading,
    systemSettings: state.auth.systemSettings,
    user: state.auth.user,
  }));

  const routes = all_routes;
  const bigImgSliderRef = useRef(null);
  const thumbnailSliderRef = useRef(null);
  const [selectedCar, setSelectedCar] = useState({});
  const popoverRef = useRef(null);
  const [localMessage, setLocalMessage] = useState('');
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [isExpandedNotes, setIsExpandedNotes] = useState(true);

  const getParam = (key: string) => {
    const value = queryParams.get(key);
    return value ? decodeURIComponent(value) : null;
  };
  const today = new Date();

  // Get tomorrow's date
  const tomorrow = new Date();
  tomorrow.setDate(
    tomorrow.getDate() + parseInt(systemSettings.booking_min_duration ?? 7)
  );

  const [startDate, setStartDate] = useState<Date | null>(
    getParam('pickupDate') ? dayjs(getParam('pickupDate'))?.toDate() : today
  );

  const [endDate, setEndDate] = useState<Date | null>(
    getParam('pickupTime') ? dayjs(getParam('returnDate'))?.toDate() : tomorrow
  );

  const [fromTime, setFromTime] = useState(
    getParam('pickupTime') ?? systemSettings?.booking_start_time
  );
  const [untilTime, setUntilTime] = useState(
    getParam('returnTime') ?? systemSettings?.booking_end_time
  );

  const [showCalendar, setShowCalendar] = useState(false);

  useEffect(() => {
    if (vin && startDate && endDate && fromTime && untilTime) {
      dispatch(
        getVehicleByVin({
          vin: vin,
          from_date: formatDateWithTime(startDate, fromTime),
          to_date: formatDateWithTime(endDate, untilTime),
        })
      );
    }
  }, [vin, startDate, endDate, fromTime, untilTime]);

  const closeModal = () => {
    const modalElement = document.getElementById('enquiry');
    const confirmModal = document.getElementById('confirm');

    if (confirmModal) {
      confirmModal.style.display = 'block';
      confirmModal.classList.add('show', 'modal-open');
    }

    if (modalElement) {
      modalElement.style.display = 'none';
      modalElement.classList.remove('show', 'modal-open');
    }

    document.body.classList.remove('modal-open');
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  };

  const closeConfirmModal = () => {
    const backdrop = document.querySelector('.modal-backdrop');
    const confirmModal = document.getElementById('confirm');

    if (confirmModal) {
      confirmModal.style.display = 'none';
      confirmModal.classList.remove('show', 'modal-open');
    }
    if (backdrop) {
      backdrop.remove();
    }
  };

  const [timeOptions, setTimeOptions] = useState<string[]>([]);

  useEffect(() => {
    if (
      systemSettings?.booking_start_time &&
      systemSettings?.booking_end_time
    ) {
      const options = generateTimeOptions(
        systemSettings.booking_start_time,
        systemSettings.booking_end_time,
        0
      );
      setTimeOptions(options);
    }
  }, [systemSettings]);

  const [availabilityStartDate, setAvailabilityStartDate] =
    useState<Date | null>(
      getParam('pickupDate') ? dayjs(getParam('pickupDate')).toDate() : today
    );

  const [availabilityEndDate, setAvailabilityEndDate] = useState<Date | null>(
    getParam('pickupTime') ? dayjs(getParam('returnDate')).toDate() : tomorrow
  );

  const [availabilityFromTime, setAvailabilityFromTime] = useState(
    getParam('pickupTime') ?? systemSettings?.booking_start_time
  );
  const [availabilityUntilTime, setAvailabilityUntilTime] = useState(
    getParam('returnTime') ?? systemSettings?.booking_end_time
  );

  useEffect(() => {
    if (startDate && endDate) {
      setAvailabilityStartDate(startDate);
      setAvailabilityEndDate(startDate);
    }

    if (fromTime && untilTime) {
      setAvailabilityFromTime(fromTime);
      setAvailabilityUntilTime(untilTime);
    }
  }, [startDate, endDate, fromTime, untilTime]);

  useEffect(() => {
    if (bigImgSliderRef.current && thumbnailSliderRef.current) {
      bigImgSliderRef.current.slickGoTo(0);
      thumbnailSliderRef.current.slickGoTo(0);
    }
  }, []);
  useEffect(() => {
    Aos.init({ duration: 1200, once: true });
  }, []);

  const [nav2, setNav2] = useState(null);
  const sliderRef1 = useRef(null);
  const sliderRef2 = useRef(null);

  useEffect(() => {
    setNav2(sliderRef2.current);
  }, []);

  const settings1 = {
    dots: false,
    arrows: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    asNavFor: nav2 || undefined,
    ref: (slider: any) => (sliderRef1.current = slider),
  };

  type FormValues = {
    name: string;
    email: string;
    message: string;
    phone: string;
     is_sms_alert_approved: boolean;
  };

  const schema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    message: Yup.string(),
  phone: Yup.string().when('$phone', {
       is: (value: any) => value?.trim().length > 0, // Check if phone is not empty
       then: (schema) => schema.length(10, 'Phone must be exactly 10 digits'),
       otherwise: (schema) => schema.notRequired(), // Don't validate if empty
     }),
     is_sms_alert_approved: Yup.boolean().notRequired(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset: contactReset,
    // watch,
    // setValue,
    // trigger,
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: user?.last_name
        ? user?.first_name + ' ' + user?.last_name
        : user?.first_name,
      email: user?.email,
      message: '',
      phone: user?.mobile,
        is_sms_alert_approved: false,
    },
  });

  const [copySuccess, setCopySuccess] = useState('');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}${location.pathname}${location.search}`
      );
      setCopySuccess('Copied!');
    } catch (err) {
      setCopySuccess('Failed to copy!');
    }

    setTimeout(() => setCopySuccess(''), 2000);
  };

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    const currentUrl = `${window.location.origin}${location.pathname}${location.search}`;

    dispatch(
      addContactUs({
        ...data,
            is_sms_alert_approved: data?.is_sms_alert_approved?1:0,
        query_type: 'enquiry',
        vehicle_id: carDetail?.id,
        ...(carDetail?.vehicle_current_status === 'Booked' && {
          start_date: formatDateWithTime(
            availabilityStartDate,
            availabilityFromTime
          ),
          end_date: formatDateWithTime(
            availabilityEndDate,
            availabilityUntilTime
          ),
          query_type: 'check_availability',
        }),
        fairental_message: `Hi Fairental team, we have a car enquiry from a customer ${data?.name}, ${data.phone}, ${data.email}, Enquiry :${data.message}, please click on the link to see the car details: ${currentUrl}`,
      })
    );

    const encodedMessage = encodeURIComponent(
      `Hi Fairental team, we have a car enquiry from a customer ${data?.name}, ${data.phone}, ${data.email}, Enquiry :${data.message}, please click on the link to see the car details: ${currentUrl}`
    );

    const value = {
      encodedMessage: encodedMessage,
      timestamp: new Date().getTime(),
      currentUrl: currentUrl,
    };

    sessionStorage.setItem('contactForm', JSON.stringify(value));
  };

  useEffect(() => {
    const data = sessionStorage.getItem('contactForm');

    if (data) {
      const parsedData = JSON.parse(data);
      const now = new Date().getTime();
      const oneHour = 60 * 60 * 1000;

      if (now - parsedData.timestamp > oneHour) {
        sessionStorage.removeItem('contactForm');
      }

      if (
        parsedData.currentUrl ===
        `${window.location.origin}${location.pathname}${location.search}`
      ) {
        setLocalMessage(parsedData.encodedMessage);
      }
    } else {
      setLocalMessage('');
    }
  }, [sessionStorage.getItem('contactForm')]);

  useEffect(() => {
    if (contactUsSuccess) {
      showToast(contactUsSuccess);
      closeModal();
      dispatch(resetContactUs());
    } else if (contactUsError) {
      showToast(contactUsError);
      dispatch(resetContactUs());
    }
  }, [contactUsSuccess, contactUsError]);

  const [toastMessage, setToastMessage] = useState('');
  const [toastClass, setToastClass] = useState('bg-success text-white');

  const cleanHtml = DOMPurify.sanitize(`${carDetail?.description || ''}</p>`);

  const showToast = (message: any) => {
    setToastClass(
      contactUsError ? 'bg-danger text-white' : 'bg-success text-white'
    );

    setToastMessage(message);
  };

  const handleCloseToast = () => {
    setToastMessage('');
  };

  useEffect(() => {
    if (toastMessage) {
      if (toastClass === 'bg-danger text-white') {
        toast.error(toastMessage, {
          position: 'top-right',
          hideProgressBar: true,
          onClose: handleCloseToast,
          progress: undefined,
          toastId: '',
        });
      } else {
        toast.success(toastMessage, {
          position: 'top-right',
          hideProgressBar: true,
          onClose: handleCloseToast,
          progress: undefined,
          toastId: '',
        });
      }
    }
  }, [toastMessage, toastClass]);

  useEffect(() => {
    if (user) {
      contactReset({
        name: user.name ?? '',
        email: user.email ?? '',
        phone: user.mobile ?? '',
        message: '',
      });
    }
  }, [user, contactReset, contactUsSuccess]);

  const DetailButton = () => {
    return (
      <>
        <div className="  mt-2">
          {carDetail?.vehicle_current_status === 'Booked' ? (
            <Link
              to={`${routes.listingGrid}?pickupDate=${dayjs(startDate).format('YYYY-MM-DD')}&pickupTime=${fromTime}&returnDate=${dayjs(endDate).format('YYYY-MM-DD')}&returnTime=${untilTime}`}
              onClick={() => {
                dispatch(
                  setSidebarFilters({
                    body_style: carDetail?.body_style,
                    make: carDetail?.make,
                    price: selectedFilters?.price,
                    vehicle_id: carDetail.id,
                  })
                );
              }}
              className="btn btn-primary py-1"
            >
              Similar Cars
            </Link>
          ) : (
            <>
              {carDetail?.price_per_day ? (
                <Link
                  to={`${routes.bookingCheckout}/${vin}?pickupDate=${dayjs(startDate).format('YYYY-MM-DD')}&pickupTime=${fromTime}&returnDate=${dayjs(endDate).format('YYYY-MM-DD')}&returnTime=${untilTime}`}
                  className="btn btn-primary  py-1  w-100"
                >
                  Book
                </Link>
              ) : (
                <span className="btn btn-secondary  py-1 w-100 disabled">
                  Book
                </span>
              )}
            </>
          )}

          {localMessage ? (
            <Link
              to="#"
              className="btn btn-secondary w-100"
              style={{
                cursor: 'not-allowed',
                pointerEvents: 'none',
                opacity: 0.6,
              }}
            >
              Thanks! We will get back to soon
            </Link>
          ) : (
            <Link
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#enquiry"
              className="btn btn-secondary w-100"
            >
              {carDetail?.vehicle_current_status === 'Booked'
                ? 'Check Availability'
                : 'Get in touch'}
            </Link>
          )}
        </div>
      </>
    );
  };

  return (
    <div className=" px-2">
      <>
        {toastMessage && <ToastContainer autoClose={3000} />}

        {loading ? (
          <>
            <section className=" product-detail-head">
              <div className="d-flex justify-content-center align-items-center">
                <Lottie
                  animationData={Loader}
                  loop={true}
                  style={{ width: 300, height: '50vh' }}
                />
              </div>
            </section>
          </>
        ) : (
          <>
            <section
              className="product-detail-head"
              style={{
                padding: '14px 0px 9px',
              }}
            >
              <div className="container">
                <div className="sign-group py-1">
                  <Link
                    to={routes.listingGrid}
                    className="border-0 text-primary-dark d-flex align-items-center mb-0"
                  >
                    <i className="bx bx-arrow-back fs-5 me-2"></i>

                    <span className=""> Back </span>
                  </Link>
                </div>

                <div className="detail-page-head">
                  <div className="detail-headings w-100">
                    <div className="star-rated">
                      <div className="camaro-info  d-flex justify-content-between align-items-center">
                        <h4 className="text-primary-dark">
                          {carDetail?.vehicle_name}{' '}
                        </h4>

                        {copySuccess ? (
                          <FaCheck color="green" fontSize={24} />
                        ) : (
                          <FiLink
                            className="text-primary-dark"
                            fontSize={24}
                            onClick={handleCopy}
                            style={{ cursor: 'pointer' }}
                          />
                        )}
                      </div>

                      <ul className="list-rating mt-2">
                        <li>
                          <span className="year bg-primary text-white">
                            {'$' + (carDetail?.price_per_day || 0) + ' / Day'}
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            {/* /Detail Page Head*/}

            <section className="section product-details pt-0">
              <div className="container">
                <div className="row">
                  <div className="col-lg-8 p-0">
                    <div className="detail-product py-0">
                      <div className="slider detail-bigimg">
                        {carDetail?.images?.length > 0 ? (
                          <Slider {...settings1}>
                            {carDetail?.images?.map(
                              (vehicle: any, index: any) => (
                                <div
                                  className="product-img fit-image-container"
                                  key={index}
                                  data-bs-toggle="modal"
                                  data-bs-target="#car_image"
                                  onClick={() => {
                                    setSelectedCar({
                                      name: carDetail?.vehicle_name,
                                      image: vehicle,
                                    });
                                  }}
                                >
                                  <ImageWithBasePath
                                    src={vehicle}
                                    alt={`Car Image ${index + 1}`}
                                  />
                                </div>
                              )
                            )}
                          </Slider>
                        ) : (
                          <div className="product-img fit-image-container">
                            <ImageWithBasePath
                              src={'assets/img/cars/no_image.svg'}
                              alt={`Car Image`}
                            />
                          </div>
                        )}

                        {carDetail?.vehicle_current_status === 'Booked' && (
                          <div className="feature-text">
                            <span>{carDetail?.vehicle_current_status}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Listing Section */}
                    <div className="review-sec px-2 mx-1 my-0  pt-1 pb-1">
                      <div className="review-header border-0 mb-0">
                        {/* <h4>Car & Rental Details</h4> */}

                        <div className="d-md-none">
                          <p
                            className="mb-0"
                            style={{
                              fontSize: '14px',
                              borderRadius: 10,
                              padding: '8px',
                              boxShadow: 'none',
                              border: '1px solid rgba(220, 220, 220, 0.55)',
                            }}
                            onClick={() => setOpen(!open)}
                          >
                            {startDate && endDate
                              ? `${dayjs(startDate).format('MMM DD')} ${fromTime} - ${dayjs(endDate).format('MMM DD')} ${untilTime}`
                              : 'Select Date'}
                          </p>

                          {open && (
                            <div
                              className="position-absolute"
                              ref={popoverRef}
                              style={{
                                width: '90%',
                                background: '#fff',
                                boxShadow: '0px 4px 10px rgba(0,0,0,0.15)',
                                borderRadius: '8px',
                                padding: '12px 0',
                                zIndex: 99,
                              }}
                            >
                              <div className="w-100 p-2">
                                <div>
                                  <span
                                    className="d-flex float-end text-end"
                                    onClick={() => {
                                      setOpen(!open);
                                    }}
                                  >
                                    {' '}
                                    <i className="feather icon-x fs-5 " />
                                  </span>
                                </div>

                                <div className="mt-1">
                                  <label
                                    htmlFor="calendar-12h "
                                    className="explore-text    block mb-0"
                                    style={{ fontSize: '14px' }}
                                  >
                                    {' '}
                                    From{' '}
                                  </label>

                                  <div className="d-flex gap-2">
                                    <Calendar
                                      minDate={new Date()}
                                      className="datetimepicker"
                                      value={startDate} // Should be a Date object
                                      onChange={(e) => {
                                        const selectedDate = e.value as Date;

                                        setStartDate(selectedDate);

                                        if (
                                          selectedDate &&
                                          systemSettings?.booking_min_duration
                                        ) {
                                          const duration = parseInt(
                                            systemSettings.booking_min_duration
                                          );
                                          const calculatedEndDate = new Date(
                                            new Date(selectedDate).setDate(
                                              selectedDate.getDate() + duration
                                            )
                                          );
                                          setEndDate(calculatedEndDate);
                                        } else {
                                          setEndDate(undefined);
                                        }
                                      }} // Ensure e.value is a Date
                                      onHide={() => setShowCalendar(false)}
                                      visible={showCalendar}
                                      readOnlyInput
                                    />
                                    <select
                                      value={fromTime}
                                      onChange={(e) => {
                                        setFromTime(e.target.value);
                                        setUntilTime(e.target.value);
                                      }}
                                      className="form-select dynamic-select w-50   px-2"
                                    >
                                      {timeOptions.map((time, index) => (
                                        <option key={index} value={time}>
                                          {time}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                                <div className="">
                                  <label
                                    htmlFor="calendar-12h"
                                    className="explore-text   block mb-0"
                                    style={{
                                      fontSize: '14px',
                                    }}
                                  >
                                    {' '}
                                    Until{' '}
                                  </label>
                                  <div className="d-flex gap-2">
                                    <Calendar
                                      className="datetimepicker "
                                      value={endDate} // Should be a Date object
                                      minDate={
                                        startDate &&
                                        systemSettings?.booking_min_duration
                                          ? new Date(
                                              new Date(startDate).setDate(
                                                new Date(startDate).getDate() +
                                                  parseInt(
                                                    systemSettings.booking_min_duration
                                                  )
                                              )
                                            )
                                          : undefined
                                      }
                                      onChange={(e) => {
                                        setEndDate(e.value);
                                      }}
                                      onHide={() => setShowCalendar(false)}
                                      visible={showCalendar}
                                      readOnlyInput
                                    />

                                    <select
                                      value={untilTime}
                                      onChange={(e) =>
                                        setUntilTime(e.target.value)
                                      }
                                      className="form-select dynamic-select w-50   px-2"
                                    >
                                      {timeOptions.map((time, index) => (
                                        <option key={index} value={time}>
                                          {time}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>

                                {/* <div className="d-flex gap-2 justify-content-end mt-2">
                                 
 

                                </div> */}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="description-list">
                        <p>
                          <span className="fw-bold text-primary-dark ">
                            {' '}
                            Price{' '}
                          </span>{' '}
                          ${carDetail?.price_per_day} per day
                        </p>
                        <p>
                          <span className="fw-bold text-primary-dark ">
                            {' '}
                            Pickup{' '}
                          </span>{' '}
                          <i className="bx bx-map" />{' '}
                          {systemSettings?.default_pickup_location}
                        </p>

                        <div className="location-content pb-2 d-md-none ">
                          <div className="delivery-tab">
                            <div className="input-block mb-0">
                              <div className="search-btn px-0">
                                <DetailButton />
                              </div>
                            </div>
                          </div>
                        </div>

                        <p className="mb-0 text-justify">
                          <span className="fw-bold text-primary-dark mb-1">
                            Details :{' '}
                          </span>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: isExpandedNotes
                                ? cleanHtml.substring(0, isMobile ? 180 : 400) +
                                  '...'
                                : cleanHtml,
                            }}
                          ></div>

                          <a
                            href="#!"
                            onClick={() => setIsExpandedNotes(!isExpandedNotes)}
                            className="text-nowrap"
                          >
                            {isExpandedNotes ? 'Read More...' : 'Read Less'}
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-4 theiaStickySidebar p-0">
                    <div className="stickybar">
                      <div className="review-sec m-0 py-0 px-2  ">
                        <p
                          className=" d-none d-md-block "
                          style={{
                            fontSize: '14px',
                            borderRadius: 10,
                            padding: '8px',
                            boxShadow: 'none',
                            border: '1px solid rgba(220, 220, 220, 0.55)',
                          }}
                          onClick={() => setOpen(!open)}
                        >
                          {startDate && endDate
                            ? `${dayjs(startDate).format('MMM DD')} ${fromTime} - ${dayjs(endDate).format('MMM DD')} ${untilTime}`
                            : 'Select Date'}
                        </p>

                        <div className=" d-none d-md-block ">
                          {open && (
                            <div
                              className="position-absolute"
                              ref={popoverRef}
                              style={{
                                width: '90%',
                                background: '#fff',
                                boxShadow: '0px 4px 10px rgba(0,0,0,0.15)',
                                borderRadius: '8px',
                                padding: '12px 0',
                                zIndex: 99,
                              }}
                            >
                              <div className="w-100 p-2">
                                <div>
                                  <span
                                    className="d-flex float-end text-end"
                                    onClick={() => {
                                      setOpen(!open);
                                    }}
                                  >
                                    {' '}
                                    <i className="feather icon-x fs-5 " />
                                  </span>
                                </div>

                                <div className="mt-1">
                                  <label
                                    htmlFor="calendar-12h "
                                    className="explore-text    block mb-0"
                                    style={{ fontSize: '14px' }}
                                  >
                                    {' '}
                                    From{' '}
                                  </label>

                                  <div className="d-flex gap-2">
                                    <Calendar
                                      minDate={new Date()}
                                      className="datetimepicker"
                                      value={startDate}
                                      onChange={(e) => {
                                        const selectedDate = e.value as Date;

                                        setStartDate(selectedDate);

                                        if (
                                          selectedDate &&
                                          systemSettings?.booking_min_duration
                                        ) {
                                          const duration = parseInt(
                                            systemSettings.booking_min_duration
                                          );
                                          const calculatedEndDate = new Date(
                                            new Date(selectedDate).setDate(
                                              selectedDate.getDate() + duration
                                            )
                                          );
                                          setEndDate(calculatedEndDate);
                                        }
                                      }}
                                      onHide={() => setShowCalendar(false)}
                                      visible={showCalendar}
                                      readOnlyInput
                                    />
                                    <select
                                      value={fromTime}
                                      onChange={(e) => {
                                        setFromTime(e.target.value);
                                        setUntilTime(e.target.value);
                                      }}
                                      className="form-select dynamic-select w-50   px-2"
                                    >
                                      {timeOptions.map((time, index) => (
                                        <option key={index} value={time}>
                                          {time}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                                <div className="">
                                  <label
                                    htmlFor="calendar-12h"
                                    className="explore-text   block mb-0"
                                    style={{
                                      fontSize: '14px',
                                    }}
                                  >
                                    {' '}
                                    Until{' '}
                                  </label>
                                  <div className="d-flex gap-2">
                                    <Calendar
                                      className="datetimepicker "
                                      value={endDate}
                                      minDate={
                                        startDate &&
                                        systemSettings?.booking_min_duration
                                          ? new Date(
                                              new Date(startDate).setDate(
                                                new Date(startDate).getDate() +
                                                  parseInt(
                                                    systemSettings.booking_min_duration
                                                  )
                                              )
                                            )
                                          : undefined
                                      }
                                      onChange={(e) => {
                                        setEndDate(e.value ?? new Date());
                                      }}
                                      onHide={() => setShowCalendar(false)}
                                      visible={showCalendar}
                                      readOnlyInput
                                    />

                                    <select
                                      value={untilTime}
                                      onChange={(e) =>
                                        setUntilTime(e.target.value)
                                      }
                                      className="form-select dynamic-select w-50   px-2"
                                    >
                                      {timeOptions.map((time, index) => (
                                        <option key={index} value={time}>
                                          {time}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>

                                <div className="d-flex gap-2 justify-content-end mt-2"></div>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="location-content d-none d-md-block">
                          <div className="delivery-tab">
                            <div className="input-block mb-0">
                              <div className="search-btn px-1">
                                <DetailButton />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="review-sec m-0 px-2 px-md-3">
                        <div className="review-header px-1">
                          <h4>Policies</h4>
                        </div>
                        <div className="policy-list">
                          <div className="policy-item">
                            <div className="policy-info">
                              <p>
                                I hereby agree to the terms and conditions of
                                the Lease Agreement with Host
                              </p>
                            </div>
                            <Link
                              to="#"
                              target="_blank"
                              data-bs-toggle="modal"
                              data-bs-target="#policy-modal"
                            >
                              View Details
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}

        <PolicyForm />

        <EnquiryForm
          carDetail={carDetail}
          handleSubmit={handleSubmit}
          contactUsLoading={contactUsLoading}
          onSubmit={onSubmit}
          register={register}
          errors={errors}
          timeOptions={timeOptions}
          systemSettings={systemSettings}
          setShowCalendar={setShowCalendar}
          showCalendar={showCalendar}
          setStartDate={setAvailabilityStartDate}
          setEndDate={setAvailabilityEndDate}
          setFromTime={setAvailabilityFromTime}
          setUntilTime={setAvailabilityUntilTime}
          startDate={availabilityStartDate}
          endDate={availabilityEndDate}
          fromTime={availabilityFromTime}
          untilTime={availabilityUntilTime}
        />

        <ContactConfirm closeConfirmModal={closeConfirmModal} />

        <CarImage Car={selectedCar} />
      </>
    </div>
  );
};

export default listingDetails;
